import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, Router, withHashLocation } from '@angular/router';
import { routes } from './app.routes';

import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { accessRefreshInterceptorFn } from './interceptors/access-refresh.interceptor';
import { environment } from '../environments/environment';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  AuditServiceToken,
  CommentServiceToken,
  IssueServiceToken,
  IssueTypeServiceToken,
  ModuleServiceToken,
  ModuleTypeRelationServiceToken,
  OrganizationServiceToken,
  RelationshipServiceToken,
  RelationshipTypeServiceToken,
  ReportServiceToken,
  TenantServiceToken,
  UserServiceToken,
  WorkflowStateServiceToken,
  WorkflowTypeServiceToken,
  ModuleTypeServiceToken,
  StatusServiceToken,
  ProcessingRequestToken,
  AttachmentServiceToken,
  AttachmentManagerServiceToken,
  SavedFilterServiceToken,
  UserGroupMemberServiceToken,
  UserGroupServiceToken,
  IssueTypeCategoryServiceToken,
  TabLabelAliasServiceToken,
  IntegrationServiceToken,
} from './shared/injection-tokens/generic.injection-tokens';
import { GenericService } from './services/generic.service';
import { Endpoint } from 'utils/enums/endpoint.util';
import { provideNativeDateAdapter } from '@angular/material/core';
import { IssueService } from './services/issue.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS, MatIconRegistry } from '@angular/material/icon';
import { QuillModule } from 'ngx-quill';
import { createProvider } from './services/service-factory-creator.service';
import { requiresAuthToken } from './shared/utils/public-route.util';
import { GlobalErrorHandler } from './services/global-error-handler.service';
import { SentryGlobalErrorHandler } from './services/sentry-global-error-handler.service';
import { UserService } from './services/user.service';
import { IntegrationService } from './services/integration.service';
import { AppVersionService } from './services/app-version.service';
import * as Sentry from "@sentry/angular";

// If not in production enable NGRX store devtools
const ngrxDevtools = environment.production ? [] : provideStoreDevtools();

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(QuillModule.forRoot()),
    provideRouter(routes, withHashLocation()),

    provideAuth0({
      ...environment.auth0,
      httpInterceptor: {
        // Attach a bearer token to authenticated API calls only. `requiresAuthToken`
        // deliberately does NOT match the public `api/public/*` endpoints so they
        // are forwarded without touching the Auth0 SDK — see the helper for why
        // `allowAnonymous` is not sufficient here.
        allowedList: [{ uriMatcher: requiresAuthToken }],
      },
    }),
    // Combine both interceptors in a single provideHttpClient call
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([authHttpInterceptorFn, accessRefreshInterceptorFn])
    ),
    provideAnimationsAsync(),
    provideStore(),
    ngrxDevtools,
    provideNativeDateAdapter(),
    createProvider(AuditServiceToken, GenericService, Endpoint.AUDIT),
    createProvider(
      OrganizationServiceToken,
      GenericService,
      Endpoint.ORGANIZATION
    ),
    createProvider(UserServiceToken, UserService, Endpoint.USER),
    createProvider(TenantServiceToken, GenericService, Endpoint.TENANT),
    createProvider(IssueTypeServiceToken, GenericService, Endpoint.ISSUE_TYPE),
    createProvider(StatusServiceToken, GenericService, Endpoint.STATUS),
    createProvider(ReportServiceToken, GenericService, Endpoint.REPORT),
    createProvider(ModuleServiceToken, GenericService, Endpoint.MODULE),
    createProvider(IssueServiceToken, IssueService, Endpoint.ISSUE),
    createProvider(
      RelationshipServiceToken,
      GenericService,
      Endpoint.RELATIONSHIP
    ),
    createProvider(
      RelationshipTypeServiceToken,
      GenericService,
      Endpoint.RELATIONSHIP_TYPE
    ),
    createProvider(
      ModuleTypeRelationServiceToken,
      GenericService,
      Endpoint.MODULE_TYPE_RELATIONSHIP
    ),
    createProvider(CommentServiceToken, GenericService, Endpoint.COMMENTS),
    createProvider(
      WorkflowTypeServiceToken,
      GenericService,
      Endpoint.WORKFLOW_TYPE
    ),
    createProvider(
      WorkflowStateServiceToken,
      GenericService,
      Endpoint.WORKFLOW_STATE
    ),
    createProvider(
      ModuleTypeServiceToken,
      GenericService,
      Endpoint.MODULE_TYPE
    ),
    createProvider(
      SavedFilterServiceToken,
      GenericService,
      Endpoint.SAVED_FILTER
    ),
    createProvider(AttachmentServiceToken, GenericService, Endpoint.ATTACHMENT),
    createProvider(AttachmentManagerServiceToken, GenericService, Endpoint.ATTACHMENT),
    createProvider(
      ProcessingRequestToken,
      GenericService,
      Endpoint.PROCESSING_REQUEST
    ),
    createProvider(UserGroupServiceToken, GenericService, Endpoint.USER_GROUP),
    createProvider(
      UserGroupMemberServiceToken,
      GenericService,
      Endpoint.USER_GROUP_MEMBER
    ),

    createProvider(
      IssueTypeCategoryServiceToken,
      GenericService,
      Endpoint.ISSUE_TYPE_CATEGORY
    ),
    createProvider(
      TabLabelAliasServiceToken,
      GenericService,
      Endpoint.TAB_LABEL_ALIAS
    ),
    createProvider(
      IntegrationServiceToken,
      IntegrationService,
      Endpoint.INTEGRATION
    ),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-rounded' },
    },
    provideAppInitializer(() => {
      inject(MatIconRegistry).registerFontClassAlias(
        'material-symbols-rounded',
        'material-symbols-rounded mat-ligature-font',
      );
    }),
    // A tab left open across a deploy moves to the new build on its next
    // navigation instead of running the old one against the new API.
    provideAppInitializer(() => inject(AppVersionService).start()),

    // Conditionally add Sentry providers based on environment.sentry.enabled
    ...(environment.sentry?.enabled
      ? [
          GlobalErrorHandler,
          {
            provide: ErrorHandler,
            useClass: SentryGlobalErrorHandler,
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          provideAppInitializer(() => {
            inject(Sentry.TraceService);
          }),
        ]
      : [
          { provide: ErrorHandler, useClass: GlobalErrorHandler },
        ]),
  ],
};
